import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/constants";
import Seo from "@/components/Seo";
import SmartImage from "@/components/SmartImage";
import { useBasket } from "@/context/BasketContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const SORTS = [
  ["category", "Sort · Category"],
  ["az", "Sort · Name A–Z"],
  ["za", "Sort · Name Z–A"],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState(searchParams.get("category") || "All");
  const [sort, setSort] = useState("category");
  const [loaded, setLoaded] = useState(false);
  const { add, has } = useBasket();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        setProducts(r.data.products || []);
        setCategories(r.data.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const counts = useMemo(() => {
    const c = {};
    products.forEach((p) => {
      c[p.category] = (c[p.category] || 0) + 1;
    });
    return c;
  }, [products]);

  const visible = useMemo(() => {
    let list = cat === "All" ? [...products] : products.filter((p) => p.category === cat);
    if (sort === "az") list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "za") list.sort((a, b) => b.name.localeCompare(a.name));
    else list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name));
    return list;
  }, [products, cat, sort]);

  return (
    <main className="bg-white text-kera-ink font-body min-h-screen" data-testid="products-page">
      <Seo title="Our Products" description="Browse the KERA range — frozen snacks, porotta, idiyappam, ready-to-cook vegetables, curries, lentils, pickles and rice for wholesale across Europe." path="/products" />
      <Navbar />

      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pt-28 pb-20">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-kera-copper mb-4"
        >
          The KERA Range
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.08 }}
          className="font-display font-bold tracking-tight text-kera-ink text-4xl sm:text-5xl"
          data-testid="products-page-title"
        >
          Our Products
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16 }}
          className="mt-4 max-w-xl text-base text-kera-muted leading-relaxed"
        >
          Authentic Kerala favourites — snapped frozen at peak freshness and delivered across Europe.
        </motion.p>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6 justify-between border-y border-kera-line py-4" data-testid="products-filter-bar">
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="category-chips">
            {["All", ...categories].map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`cat-chip-${slug(c)}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-[11px] font-mono uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  cat === c
                    ? "bg-black text-white border-black"
                    : "border-kera-line text-kera-muted hover:border-kera-gold hover:text-kera-copper"
                }`}
              >
                {c}
                {c !== "All" && counts[c] ? <span className="ml-1.5 text-kera-gold">{counts[c]}</span> : null}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted" data-testid="products-count">
              {visible.length} item{visible.length === 1 ? "" : "s"}
            </span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="products-sort-select"
              className="rounded-full border border-kera-line bg-white px-4 py-2 text-[11px] font-mono uppercase tracking-[0.15em] text-kera-ink outline-none focus:border-kera-gold cursor-pointer"
            >
              {SORTS.map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {visible.length > 0 ? (
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8" data-testid="products-grid">
            {visible.map((p, i) => {
              const inBasket = has(p.id);
              return (
                <motion.div
                  key={p.id || p.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  className="group"
                  data-testid={`product-tile-${slug(p.name)}`}
                >
                  <div className="relative rounded-2xl overflow-hidden border border-kera-line bg-[#FAF7F0]">
                    <Link to={`/products/${p.id}`} data-testid={`product-link-${slug(p.name)}`}>
                      <SmartImage
                        src={p.image}
                        alt={`KERA ${p.name}`}
                        sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                        className="w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </Link>
                    <button
                      onClick={() => {
                        if (!inBasket) {
                          add(p);
                          toast.success(`${p.name} added to your enquiry list`);
                        }
                      }}
                      aria-label={inBasket ? `${p.name} in enquiry list` : `Add ${p.name} to enquiry list`}
                      data-testid={`product-add-${slug(p.name)}`}
                      className={`absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center shadow-md transition-colors duration-300 ${
                        inBasket ? "bg-kera-gold text-black" : "bg-white/95 text-kera-ink hover:bg-black hover:text-kera-gold"
                      }`}
                    >
                      {inBasket ? <Check size={15} /> : <Plus size={15} />}
                    </button>
                  </div>
                  <Link to={`/products/${p.id}`}>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper">{p.category}</p>
                    <h3 className="font-display font-semibold text-base sm:text-lg text-kera-ink leading-snug group-hover:text-kera-copper transition-colors">
                      KERA {p.name}
                    </h3>
                    {p.description && (
                      <p className="mt-1 text-xs sm:text-sm text-kera-muted leading-relaxed">{p.description}</p>
                    )}
                  </Link>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="mt-16 text-center py-16 border border-dashed border-kera-line rounded-2xl" data-testid="products-empty">
            <p className="font-display font-semibold text-xl text-kera-ink">Coming soon to this category</p>
            <p className="mt-2 text-sm text-kera-muted">
              {loaded ? "We're preparing something special. Check back shortly." : "Loading the range…"}
            </p>
          </div>
        )}
      </div>

      <Footer />
    </main>
  );
}
